import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Loader2,
  Truck,
  Lock,
  Phone,
  CreditCard,
  Smartphone,
  Banknote,
  Landmark,
  Home,
  Building2,
  Plus,
  Pencil,
  MapPin,
  ArrowLeft,
  BadgeCheck,
} from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCheckoutStep } from "@/contexts/CheckoutStepContext";

const LS_DCR_KEY = "dyntra_dcr_id_v1";
import { sendOtp as sendOtpApi, verifyOtp as verifyOtpApi } from "@/lib/adapters/auth";
import { createOrder } from "@/lib/adapters/orders";
import { runRazorpayPayment, loadRazorpayScript } from "@/lib/payments/razorpay";
import { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const LS_ADDRESSES = "dyntra_addresses_v1";
const LS_PHONE_VERIFIED = "dyntra_phone_verified_v1";

function loadAddresses() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_ADDRESSES) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

const DELIVERY_OPTIONS = [
  {
    id: "standard",
    label: "Standard Delivery",
    price: 0,
    eta: () => humanDate(5),
    perk: "Free shipping",
    tag: "FREE",
    tone: "green",
  },
  {
    id: "priority",
    label: "Priority Delivery",
    price: 149,
    eta: () => humanDate(2),
    perk: "Faster delivery to your doorstep",
    tag: "₹149",
    tone: "default",
  },
];

const PAYMENT_TILES = [
  {
    id: "upi",
    label: "UPI",
    desc: "Instant payment",
    icon: Smartphone,
    tag: "Recommended",
  },
  { id: "card", label: "Card", desc: "Credit / Debit", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", desc: "All major banks", icon: Landmark },
  { id: "cod", label: "Cash on Delivery", desc: "Pay on delivery", icon: Banknote },
];

const UPI_STRIP = ["G Pay", "PhonePe", "Paytm", "BHIM"];

function humanDate(offsetDays) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  try {
    return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function Checkout() {
  const { cart, clearCart, syncLocalToServer } = useCart();
  const { user, refresh: refreshAuth } = useAuth();
  const { setStep: setHeaderStep } = useCheckoutStep();
  const nav = useNavigate();

  const [addresses, setAddresses] = useState(loadAddresses());
  const [selectedAddress, setSelectedAddress] = useState(
    () => loadAddresses().find((a) => a.is_default)?.id || null
  );
  const [deliveryOption, setDeliveryOption] = useState("standard");
  const [payment, setPayment] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [upiVerified, setUpiVerified] = useState(false);
  const [agree, setAgree] = useState(true);
  const [loading, setLoading] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);

  // OTP inline mini-modal state
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpMock, setOtpMock] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(
    () => localStorage.getItem(LS_PHONE_VERIFIED) === "1"
  );

  useEffect(() => {
    setHeaderStep(payment ? "payment" : "delivery");
  }, [payment, setHeaderStep]);

  useEffect(() => {
    if (payment !== "cod") loadRazorpayScript().catch(() => {});
  }, [payment]);

  const activeAddress = useMemo(
    () => addresses.find((a) => a.id === selectedAddress) || addresses[0] || null,
    [addresses, selectedAddress]
  );

  const itemTotal = cart.subtotal || 0;
  const dOpt = DELIVERY_OPTIONS.find((o) => o.id === deliveryOption);
  const deliveryFee = dOpt?.price || 0;
  const gst = Math.round(itemTotal * 0.05);
  const total = itemTotal + deliveryFee + gst;

  if (!cart.items?.length) {
    return (
      <main className="dyntra-container py-16 text-center">
        <p className="text-gray-500">Your cart is empty.</p>
        <Link to="/" className="dyntra-btn-primary mt-4 inline-flex">
          Continue Shopping
        </Link>
      </main>
    );
  }

  const requestOtp = async () => {
    if (!activeAddress?.phone) {
      toast.error("Add a phone number in your address first.");
      return;
    }
    setOtpSending(true);
    try {
      const data = await sendOtpApi(activeAddress.phone);
      setOtpOpen(true);
      if (data?.mock && data?.mock_code) {
        setOtpMock(data.mock_code);
        toast.info(`MOCK OTP: ${data.mock_code}`);
      } else {
        toast.success("OTP sent");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (otpValue.length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setOtpSending(true);
    try {
      await verifyOtpApi(activeAddress.phone, otpValue);
      await refreshAuth();
      await syncLocalToServer();
      setPhoneVerified(true);
      localStorage.setItem(LS_PHONE_VERIFIED, "1");
      setOtpOpen(false);
      toast.success("Phone verified!");
      // Immediately continue with the order.
      await placeOrder();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOtpSending(false);
    }
  };

  const canPay = () => {
    if (!activeAddress) {
      toast.error("Please add a delivery address.");
      return false;
    }
    if (!agree) {
      toast.error("Please accept the Terms & Conditions.");
      return false;
    }
    if (payment === "upi" && !upiVerified && !upiId.trim()) {
      toast.error("Please enter and verify your UPI ID.");
      return false;
    }
    return true;
  };

  const startPay = async () => {
    if (!canPay()) return;
    if (!user) {
      toast.error("Please sign in to continue.");
      return;
    }
    if (!phoneVerified) {
      await requestOtp();
      return;
    }
    await placeOrder();
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      const data = await createOrder({
        items: cart.items.map((i) => ({
          product_id: i.product.id,
          quantity: i.quantity,
        })),
        address: {
          full_name: activeAddress.name,
          phone: activeAddress.phone,
          line1: activeAddress.line1,
          line2: activeAddress.line2,
          city: activeAddress.city,
          state: activeAddress.state,
          pincode: activeAddress.pincode,
        },
        phone_verified: phoneVerified,
        payment_method: payment,
      });

      if (payment !== "cod") {
        toast.info("Opening secure payment…");
        try {
          const result = await runRazorpayPayment({
            paymentInit: data.payment,
            amountMinor: Math.round(total * 100),
            orderNumber: data.order_no,
            customer: {
              name: activeAddress.name,
              email: user?.email,
              phone: activeAddress.phone,
            },
          });
          if (result?.mock) toast.info("Test payment simulated.");
          else toast.success("Payment successful!");
        } catch (err) {
          if (err?.dismissed)
            toast.info("Payment cancelled — your order is saved.");
          else toast.error(err?.message || "Payment failed. Please try again.");
          setLoading(false);
          return;
        }
      }

      await clearCart();
      nav(`/order/${data.order_no}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verifyUpi = () => {
    if (!upiId.trim()) {
      toast.error("Enter your UPI ID first.");
      return;
    }
    if (!/^[\w.-]+@[\w.-]+$/.test(upiId.trim())) {
      toast.error("UPI ID looks incorrect.");
      return;
    }
    setUpiVerified(true);
    toast.success("UPI ID verified");
  };

  const dcrId =
    typeof window !== "undefined" ? localStorage.getItem(LS_DCR_KEY) : "";

  return (
    <div className="dyntra-container py-6" data-testid="checkout-page">
      <nav className="text-xs text-gray-500 mb-3 flex items-center gap-1">
        <Link to="/" className="hover:text-[#4B2AAD]">Home</Link>
        <span>/</span>
        <Link to="/cart" className="hover:text-[#4B2AAD]">Cart</Link>
        <span>/</span>
        <span className="text-gray-900 font-semibold">Checkout</span>
      </nav>

      <div className="mb-5">
        <h1
          data-testid="checkout-title"
          className="font-display text-3xl sm:text-4xl font-extrabold text-gray-900"
        >
          Checkout
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Review your delivery and payment details.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          {/* 1. Delivery Address */}
          <SectionCard step={1} title="Delivery Address" done={!!activeAddress}>
            {addresses.length === 0 ? (
              <div className="text-center py-6">
                <MapPin className="h-8 w-8 text-gray-300 mx-auto" />
                <p className="text-sm text-gray-500 mt-2">
                  No saved address yet. Add one to continue.
                </p>
                <button
                  onClick={() => setAddAddressOpen(true)}
                  data-testid="ck-add-address"
                  className="dyntra-btn-primary mt-3"
                >
                  <Plus className="h-4 w-4" /> Add Address
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {addresses.map((a) => (
                    <label
                      key={a.id}
                      className={`rounded-2xl border p-4 cursor-pointer transition-colors ${
                        selectedAddress === a.id
                          ? "border-[#4B2AAD] bg-[#F3EFFC]"
                          : "border-gray-100 hover:border-[#4B2AAD]/40"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="address"
                          checked={selectedAddress === a.id}
                          onChange={() => setSelectedAddress(a.id)}
                          className="mt-1 accent-[#4B2AAD]"
                          data-testid={`ck-addr-${a.id}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {a.type === "Clinic" ? (
                              <Building2 className="h-4 w-4 text-[#4B2AAD]" />
                            ) : (
                              <Home className="h-4 w-4 text-[#4B2AAD]" />
                            )}
                            <p className="text-sm font-bold text-gray-900">
                              {a.type}
                            </p>
                            {a.is_default && (
                              <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold px-2 py-0.5 border border-emerald-100">
                                ✓ Default
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm font-semibold text-gray-900">
                            {a.name}
                          </p>
                          <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">
                            {a.line1}
                            {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} –{" "}
                            {a.pincode}
                            <br />
                            📞 {a.phone}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-[11px]">
                            <button className="inline-flex items-center gap-1 text-[#4B2AAD] hover:underline font-semibold">
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => setAddAddressOpen(true)}
                  data-testid="ck-add-another"
                  className="mt-3 inline-flex items-center gap-1 rounded-xl border border-dashed border-[#4B2AAD]/40 bg-[#F5F3FB] px-4 py-2 text-xs font-bold text-[#4B2AAD] hover:bg-[#F3EFFC]"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Another Address
                </button>
              </>
            )}
          </SectionCard>

          {/* 2. Delivery Options */}
          <SectionCard step={2} title="Delivery Options" done={!!deliveryOption}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {DELIVERY_OPTIONS.map((d) => {
                const active = deliveryOption === d.id;
                return (
                  <label
                    key={d.id}
                    className={`rounded-2xl border p-4 cursor-pointer transition-colors ${
                      active
                        ? "border-[#4B2AAD] bg-[#F3EFFC]"
                        : "border-gray-100 hover:border-[#4B2AAD]/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="delivery"
                        checked={active}
                        onChange={() => setDeliveryOption(d.id)}
                        className="mt-1 accent-[#4B2AAD]"
                        data-testid={`ck-delivery-${d.id}`}
                      />
                      <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                        <Truck className="h-4 w-4 text-[#4B2AAD]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900">
                          {d.label}
                        </p>
                        <p
                          className={`text-sm font-bold mt-0.5 ${
                            d.tone === "green"
                              ? "text-emerald-600"
                              : "text-gray-900"
                          }`}
                        >
                          {d.tag}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {typeof d.eta === "function" ? d.eta() : d.eta}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </SectionCard>

          {/* 3. Payment */}
          <SectionCard step={3} title="Payment Method" done={!!payment}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PAYMENT_TILES.map((p) => {
                const Ico = p.icon;
                const active = payment === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPayment(p.id)}
                    data-testid={`ck-pay-${p.id}`}
                    className={`relative rounded-2xl border p-3 text-left transition-colors ${
                      active
                        ? "border-[#4B2AAD] bg-[#F3EFFC]"
                        : "border-gray-100 bg-white hover:border-[#4B2AAD]/40"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="payment"
                        checked={active}
                        onChange={() => setPayment(p.id)}
                        className="accent-[#4B2AAD]"
                      />
                      <Ico
                        className={`h-5 w-5 ${
                          active ? "text-[#4B2AAD]" : "text-gray-400"
                        }`}
                      />
                    </div>
                    <p className="mt-2 text-sm font-bold text-gray-900">
                      {p.label}
                    </p>
                    <p className="text-[10px] text-gray-500">{p.desc}</p>
                    {p.tag && (
                      <span className="mt-1 inline-flex items-center rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[9px] font-bold px-2 py-0.5 border border-[#4B2AAD]/20">
                        {p.tag}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {payment === "upi" && (
              <div className="mt-4 rounded-2xl bg-[#F5F3FB] border border-[#4B2AAD]/10 p-4">
                <p className="text-xs font-semibold text-gray-700">UPI ID</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <input
                    value={upiId}
                    onChange={(e) => {
                      setUpiId(e.target.value);
                      setUpiVerified(false);
                    }}
                    placeholder="Enter your UPI ID"
                    className="flex-1 min-w-[220px] rounded-xl border border-gray-200 bg-white focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
                    data-testid="ck-upi-input"
                  />
                  <button
                    type="button"
                    onClick={verifyUpi}
                    data-testid="ck-upi-verify"
                    className="rounded-xl border border-[#4B2AAD]/40 text-[#4B2AAD] hover:bg-white text-xs font-semibold px-4 py-2"
                  >
                    {upiVerified ? "✓ Verified" : "Verify"}
                  </button>
                </div>
                <div className="mt-4 flex items-center justify-around gap-2 flex-wrap">
                  {UPI_STRIP.map((u) => (
                    <span
                      key={u}
                      className="text-xs font-semibold text-gray-500 tracking-wide"
                    >
                      {u}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-500">
                  <Lock className="h-3 w-3" /> Encrypted and processed securely.
                </div>
              </div>
            )}

            {payment === "card" && (
              <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4 text-xs text-gray-600">
                Card details will be requested securely on Razorpay after you confirm the order.
              </div>
            )}
            {payment === "netbanking" && (
              <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4 text-xs text-gray-600">
                Bank selection is presented on Razorpay after you confirm the order.
              </div>
            )}
            {payment === "cod" && (
              <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs text-amber-800">
                Pay with cash at the time of delivery. Please keep the exact amount ready.
              </div>
            )}
          </SectionCard>

          {dcrId && (
            <div
              data-testid="ck-dcr-verified"
              className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3 flex items-center gap-2 text-xs text-emerald-700 font-semibold"
            >
              <BadgeCheck className="h-4 w-4" />
              Dental Council Registration ID verified
            </div>
          )}

          <div className="rounded-2xl border border-gray-100 bg-white p-3 flex items-center justify-between text-[11px] text-gray-500 gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-[#4B2AAD]" />
              100% secure payments · encrypted checkout
            </span>
            <div className="flex items-center gap-2">
              {["VISA", "Master", "RuPay", "UPI"].map((p) => (
                <span
                  key={p}
                  className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-bold text-gray-500"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT SUMMARY */}
        <aside className="lg:sticky lg:top-20 self-start space-y-3">
          <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-gray-900">
                Order Summary
              </h3>
              <span className="text-xs text-gray-500">{cart.count} items</span>
            </div>

            <div className="max-h-40 overflow-y-auto thin-scroll space-y-2 pr-1">
              {cart.items.map((it) => (
                <div key={it.product.id} className="flex gap-2 text-xs">
                  <img
                    src={it.product.image || "/product-placeholder.svg"}
                    alt=""
                    className="h-10 w-10 rounded-md object-contain bg-[#F5F3FB]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 line-clamp-1">
                      {it.product.name}
                    </p>
                    <p className="text-gray-500">
                      Qty {it.quantity} × ₹{it.product.price.toLocaleString()}
                    </p>
                  </div>
                  <p className="font-bold">₹{it.line_total.toLocaleString()}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-3 space-y-1 text-sm">
              <Row label="Item total" value={`₹${itemTotal.toLocaleString()}`} />
              <Row
                label="Delivery"
                value={deliveryFee === 0 ? "FREE" : `₹${deliveryFee.toLocaleString()}`}
                tone={deliveryFee === 0 ? "green" : "default"}
              />
              <Row label="GST" value={`₹${gst.toLocaleString()}`} />
              <div className="flex items-center justify-between font-display font-bold text-base pt-2 border-t border-gray-100">
                <span>Grand Total</span>
                <span className="text-gray-900">₹{total.toLocaleString()}</span>
              </div>
            </div>

            <label className="flex items-start gap-2 text-xs text-gray-600 pt-2">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 accent-[#4B2AAD] h-4 w-4"
                data-testid="ck-terms"
              />
              I agree to the{" "}
              <Link to="/policy/terms" className="text-[#4B2AAD] font-semibold">
                Terms &amp; Conditions
              </Link>{" "}
              and Privacy Policy.
            </label>

            <button
              type="button"
              onClick={startPay}
              disabled={loading}
              data-testid="ck-pay-btn"
              className="w-full dyntra-btn-primary py-3 text-base"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Lock className="h-4 w-4" /> Pay ₹{total.toLocaleString()} Securely
                </>
              )}
            </button>

            <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1">
                <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" /> Order can be
                cancelled within 30 seconds after payment
              </span>
            </div>

            <Link
              to="/cart"
              className="w-full dyntra-btn-ghost text-sm py-2 mt-1"
            >
              Back to Cart
            </Link>
          </div>

          <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-3 text-xs text-gray-700">
            <ShieldCheck className="h-4 w-4 text-[#4B2AAD] inline mr-1" /> Your
            payment is secure. We never store your card details.
          </div>
        </aside>
      </div>

      {/* OTP mini-modal */}
      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader className="items-center text-center">
            <div className="h-12 w-12 rounded-2xl bg-[#F3EFFC] flex items-center justify-center mx-auto">
              <Phone className="h-5 w-5 text-[#4B2AAD]" />
            </div>
            <DialogTitle className="font-display mt-2">
              Verify your mobile
            </DialogTitle>
            <DialogDescription className="text-xs">
              Enter the 6-digit code we sent to{" "}
              <span className="font-semibold text-gray-800">
                {activeAddress?.phone}
              </span>
              .
            </DialogDescription>
          </DialogHeader>
          {otpMock && (
            <div className="rounded-lg bg-[#FFF7E0] border border-[#FFB020]/40 px-3 py-2 text-xs text-[#8A5A00]">
              <b>Mock OTP:</b> {otpMock}
            </div>
          )}
          <input
            data-testid="ck-otp-input"
            value={otpValue}
            onChange={(e) =>
              setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="6-digit code"
            maxLength={6}
            className="w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-3 outline-none text-center tracking-widest font-bold"
          />
          <div className="flex items-center justify-between gap-2">
            <button
              className="text-xs text-[#4B2AAD] font-semibold hover:underline"
              onClick={requestOtp}
              disabled={otpSending}
            >
              Resend
            </button>
            <button
              onClick={verifyOtp}
              disabled={otpSending}
              data-testid="ck-otp-verify"
              className="dyntra-btn-primary flex-1"
            >
              {otpSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Verify & Pay"
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add address dialog */}
      <AddAddressDialog
        open={addAddressOpen}
        onOpenChange={setAddAddressOpen}
        onAdd={(a) => {
          const next = [...addresses, a];
          setAddresses(next);
          setSelectedAddress(a.id);
          try {
            localStorage.setItem(LS_ADDRESSES, JSON.stringify(next));
          } catch {}
        }}
      />
    </div>
  );
}

function SectionCard({ step, title, done, children }) {
  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-5">
      <div className="flex items-center gap-3 mb-3">
        <span
          className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
            done ? "bg-emerald-500 text-white" : "bg-[#4B2AAD] text-white"
          }`}
        >
          {done ? "✓" : step}
        </span>
        <h2 className="font-display text-base font-bold text-gray-900">
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, tone }) {
  const toneCls =
    tone === "green" ? "text-emerald-600 font-semibold" : "text-gray-900 font-semibold";
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={toneCls}>{value}</span>
    </div>
  );
}

function AddAddressDialog({ open, onOpenChange, onAdd }) {
  const [form, setForm] = useState({
    id: "",
    type: "Home",
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    phone: "",
    is_default: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.name.trim() || !form.line1.trim() || !form.city.trim() || !form.pincode.trim()) {
      toast.error("Please fill in name, address, city and pincode.");
      return;
    }
    onAdd({ ...form, id: `a${Date.now()}` });
    toast.success("Address added");
    onOpenChange(false);
    setForm({ ...form, name: "", line1: "", line2: "", city: "", state: "", pincode: "", phone: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Add New Address</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex items-center gap-3">
            <label className="text-xs font-bold uppercase text-gray-500">Type</label>
            {["Home", "Clinic", "Office", "Other"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => set("type", t)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  form.type === t
                    ? "bg-[#F3EFFC] text-[#4B2AAD] border-[#4B2AAD]"
                    : "text-gray-600 border-gray-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <Input label="Full Name*" value={form.name} onChange={(v) => set("name", v)} />
          <Input label="Phone*" value={form.phone} onChange={(v) => set("phone", v)} />
          <Input full label="Address Line 1*" value={form.line1} onChange={(v) => set("line1", v)} />
          <Input full label="Address Line 2" value={form.line2} onChange={(v) => set("line2", v)} />
          <Input label="City*" value={form.city} onChange={(v) => set("city", v)} />
          <Input label="State*" value={form.state} onChange={(v) => set("state", v)} />
          <Input label="Pincode*" value={form.pincode} onChange={(v) => set("pincode", v)} />
          <Input label="Country" value={form.country} onChange={(v) => set("country", v)} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-3">
          <button className="dyntra-btn-ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button className="dyntra-btn-primary" onClick={save}>
            Save Address
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, value, onChange, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}
